import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage.service';
import type { AuthTokens, CurrentUser, LoginRequest } from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http    = inject(HttpClient);
  private readonly router  = inject(Router);
  private readonly storage = inject(StorageService);

  private readonly _currentUser = signal<CurrentUser | null>(null);
  private readonly _loading     = signal(false);

  readonly currentUser    = this._currentUser.asReadonly();
  readonly loading        = this._loading.asReadonly();
  readonly isAuthenticated = computed(() => !!this._currentUser());
  readonly isStaff         = computed(() => this._currentUser()?.is_staff === true);

  // -------------------------------------------------------
  login(credentials: LoginRequest): Observable<AuthTokens> {
    this._loading.set(true);
    return this.http.post<AuthTokens>(`${environment.apiUrl}/users/login/`, credentials).pipe(
      tap(tokens => {
        this.storage.setAccessToken(tokens.access);
        this.storage.setRefreshToken(tokens.refresh);
        this._loading.set(false);
      }),
      catchError(err => {
        this._loading.set(false);
        return throwError(() => err);
      })
    );
  }

  // -------------------------------------------------------
  loadCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${environment.apiUrl}/users/me/`).pipe(
      tap(user => this._currentUser.set(user))
    );
  }

  // -------------------------------------------------------
  refreshToken(): Observable<AuthTokens> {
    const refresh = this.storage.getRefreshToken();
    if (!refresh) return throwError(() => new Error('No refresh token'));

    return this.http.post<AuthTokens>(`${environment.apiUrl}/users/refresh-token/`, { refresh }).pipe(
      tap(tokens => {
        this.storage.setAccessToken(tokens.access);
        if (tokens.refresh) this.storage.setRefreshToken(tokens.refresh);
      })
    );
  }

  // -------------------------------------------------------
  logout(): void {
    this._currentUser.set(null);
    this.storage.clearTokens();
    this.router.navigate(['/login']);
  }

  // -------------------------------------------------------
  loginAndRedirect(credentials: LoginRequest): Observable<CurrentUser> {
    return this.login(credentials).pipe(
      switchMap(() => this.loadCurrentUser()),
      tap(user => {
        if (user.is_staff) {
          this.router.navigate(['/usuarios']);
        } else {
          // Non-admin — clear and show error
          this.logout();
        }
      })
    );
  }
}
